import { useDeepEqualMemo } from '@carvana/merch-react-hooks';
import { createContext, useContext, useMemo, useState } from 'react';
import type React from 'react';
import type { DeviceData, NativeDeviceType, ScreenSize } from '~/contracts';

const getScreenSizeFromDeviceData = (deviceData: DeviceData): ScreenSize => ({
  height: deviceData.height,
  isDesktop: deviceData.isDesktop,
  isMobile: deviceData.isMobile,
  isTablet: deviceData.isTablet,
  width: deviceData.width
});

const defaultDeviceData: DeviceData = {
  browserName: '',
  deviceName: '',
  height: 0,
  isDesktop: false,
  isMobile: true,
  isTablet: false,
  width: 0
};

export type ContextType = {
  deviceData: DeviceData;
  nativeDeviceType: NativeDeviceType;
  screenSize: ScreenSize;
  setScreenSize: React.Dispatch<React.SetStateAction<ScreenSize>>;
};

export const defaultContext = {
  deviceData: defaultDeviceData,
  nativeDeviceType: {
    isNative: false,
    platform: null
  },
  screenSize: getScreenSizeFromDeviceData(defaultDeviceData),
  setScreenSize() {}
};

const DeviceContext = createContext<ContextType>(defaultContext);

export type OwnProps = {
  deviceData: DeviceData;
  nativeDeviceType: NativeDeviceType;
};

export const DeviceContextProvider = ({
  children,
  deviceData,
  nativeDeviceType: nativeDeviceTypeProp
}: React.PropsWithChildren<OwnProps>) => {
  const { browserName, deviceName } = deviceData ?? defaultDeviceData;
  const [nativeDeviceType] = useState(nativeDeviceTypeProp);
  const [screenSize, setScreenSize] = useState(
    getScreenSizeFromDeviceData(deviceData ?? defaultDeviceData)
  );

  const value = useMemo(
    () => ({
      deviceData,
      nativeDeviceType,
      screenSize,
      setScreenSize
    }),
    [browserName, deviceName, nativeDeviceType, screenSize]
  );

  return (
    <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
  );
};

DeviceContextProvider.displayName = 'DeviceContextProvider';

export const useDeviceData = (): DeviceData =>
  useContext(DeviceContext).deviceData;

export const useNativeDeviceType = (): NativeDeviceType =>
  useDeepEqualMemo(useContext(DeviceContext).nativeDeviceType);

export const useIsNative = (): boolean => useNativeDeviceType().isNative;

export const useScreenSize = (): ScreenSize =>
  useContext(DeviceContext).screenSize;

export const useIsMobile = (): boolean => useScreenSize().isMobile;

export default DeviceContext;
